class CarCompatible extends HTMLElement {
  constructor() {
    super();
    this.searchTag = (this.dataset.searchTag || '').trim();
    this.groupIds = (this.dataset.groupIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    this.slider = this.querySelector('[id^="Slider-"]');
    this.currentProductId = this.dataset.product || null;
    this.collectionUrl = this.dataset.collectionUrl || '/collections/all';
    this.fallbackCollectionUrl =
      this.dataset.fallbackCollectionUrl || '/collections/gerelateerde-producten-geen-automatten';
    this.section =
      this.closest('[data-car-compatible-root]') || this.closest('[id^="shopify-section-"]');
    this.maxItems = 16;
    this.hasLoaded = false;
  }

  hideSection() {
    if (!this.section) return;
    this.section.hidden = true;
  }

  showSection() {
    if (!this.section) return;
    this.section.hidden = false;
  }

  fetchView(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        return Array.from(doc.querySelectorAll('li[data-product-id]'));
      });
  }

  buildTagUrl() {
    return `${this.collectionUrl}/${this.searchTag}?view=car-compatible`;
  }

  buildGroupUrl() {
    if (this.groupIds.length === 0) return null;

    const params = new URLSearchParams();
    params.set('view', 'car-compatible');
    this.groupIds.forEach((id) => {
      const value = id.startsWith('gid://') ? id : `gid://shopify/Metaobject/${id}`;
      params.append('filter.p.m.custom.geschikt_voor_groep', value);
    });

    return `${this.collectionUrl}?${params.toString()}`;
  }

  buildFallbackUrl() {
    if (!this.fallbackCollectionUrl) return null;
    const separator = this.fallbackCollectionUrl.includes('?') ? '&' : '?';
    return `${this.fallbackCollectionUrl}${separator}view=car-compatible`;
  }

  mergeItems(...itemGroups) {
    const seen = new Set();
    const merged = [];

    itemGroups.forEach((items) => {
      items.forEach((item) => {
        const productId = item.dataset.productId;
        if (!productId) return;
        if (this.currentProductId && productId === this.currentProductId) return;
        if (seen.has(productId)) return;
        if (merged.length >= this.maxItems) return;

        seen.add(productId);
        merged.push(item);
      });
    });

    return merged;
  }

  renderItems(items) {
    if (!this.slider || items.length === 0) {
      this.hideSection();
      return;
    }

    this.slider.replaceChildren(...items);
    this.showSection();

    const sliderComponent = this.querySelector('slider-component');
    if (sliderComponent && typeof sliderComponent.resetPages === 'function') {
      requestAnimationFrame(() => {
        sliderComponent.resetPages();
      });
    }
  }

  loadFallbackProducts() {
    const fallbackUrl = this.buildFallbackUrl();
    if (!fallbackUrl) {
      this.hideSection();
      return Promise.resolve();
    }

    return this.fetchView(fallbackUrl)
      .then((items) => {
        this.renderItems(this.mergeItems(items));
      })
      .catch((error) => {
        console.error('Error loading fallback car compatible products:', error);
        this.hideSection();
      });
  }

  loadProducts() {
    if (this.hasLoaded) return;
    this.hasLoaded = true;

    if (!this.searchTag) {
      this.loadFallbackProducts();
      return;
    }

    const tagUrl = this.buildTagUrl();
    const groupUrl = this.buildGroupUrl();
    const requests = [this.fetchView(tagUrl)];

    if (groupUrl) {
      requests.push(this.fetchView(groupUrl));
    }

    Promise.all(requests)
      .then(([tagItems, groupItems = []]) => {
        const merged = this.mergeItems(tagItems, groupItems);
        if (merged.length > 0) {
          this.renderItems(merged);
          return;
        }

        return this.loadFallbackProducts();
      })
      .catch((error) => {
        console.error('Error loading car compatible products:', error);
        return this.loadFallbackProducts();
      });
  }

  connectedCallback() {
    this.loadProducts();
  }
}

customElements.define('car-compatible', CarCompatible);
